import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import EntityMenu from 'app/frontend/base/components/EntityMenu'
import EntityMenuMobileList from 'app/frontend/base/components/EntityMenuMobileList'
import { hasAccessToContent } from 'app/frontend/base/components/ProtectedContent'
import GuildContext from 'app/frontend/base/config/context/guild'
import { useTeamDetails } from 'app/frontend/base/config/queries'
import { compareTeamsByDefaultAndName } from 'app/frontend/base/helpers/teamOrdering'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import DefaultTeamStar from 'app/frontend/base/components/DefaultTeamStar'
import LinkedTeamsMenuGroups from 'app/frontend/base/components/LinkedTeamsMenuGroups'
import { linkedTeamGroups } from 'app/frontend/base/helpers/linkedGuilds'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import { useTeamMenuItems } from '../../TeamDashboard/components/TeamSubheader'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export const DEFAULT_CUSTOM_DOMAIN_MAIN_MENU_CONTENT =
  'team_switcher_and_team_menu'

export const getSelectedCustomDomainTeam = (
  customDomain?: wowaudit.CustomDomain,
) => {
  if (!customDomain?.guild) return undefined

  const team = customDomain.guild.teamReferences.find((team) => {
    const teamPath = customDomainTeamPath(customDomain, team)

    return (
      window.location.pathname === teamPath ||
      window.location.pathname.startsWith(`${teamPath}/`)
    )
  })

  return team
    ? { ...team, path: customDomainTeamPath(customDomain, team) }
    : undefined
}

export const customDomainTeamPath = (
  customDomain: wowaudit.CustomDomain,
  team: wowaudit.TeamReference,
) => {
  return customDomain.guild.path
    ? team.path
    : team.basePath.replace(customDomain.guild.basePath, '')
}

export const getCustomDomainTeamsWithAccess = (
  customDomain?: wowaudit.CustomDomain,
) => {
  if (!customDomain) return []

  return [...customDomain.guild.teamReferences]
    .filter(
      (team) =>
        hasAccessToContent('member', 'base', customDomain.guild, team)
          .hasAccess,
    )
    .sort(compareTeamsByDefaultAndName)
}

export const CustomDomainTeamSwitcher = ({
  selectedTeam,
  className,
  showSelectedTeamLabel,
}: {
  selectedTeam?: wowaudit.TeamReference
  className?: string
  showSelectedTeamLabel?: boolean
}) => {
  const { t } = useTranslation()
  const { customDomain } = useCustomDomain()
  const gameVersion = useGameVersion()

  const teamsWithAccess = getCustomDomainTeamsWithAccess(customDomain)
  const linkedGroups = linkedTeamGroups(customDomain?.guild, gameVersion.id, {
    requireAccess: true,
  })

  if (!customDomain || (!teamsWithAccess.length && !linkedGroups.length)) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span
          className={cn(
            'flex gap-1 items-center min-w-0 cursor-pointer',
            className,
          )}
        >
          <span className='flex flex-col leading-tight mr-1 min-w-0'>
            {selectedTeam && showSelectedTeamLabel ? (
              <span className='text-xs font-medium text-muted-foreground'>
                Team
              </span>
            ) : null}
            <span className='text-sm font-medium truncate sm:whitespace-nowrap'>
              {selectedTeam?.name ?? t('dashboard.team.choose-team')}
            </span>
          </span>
          <ChevronDown width={12} className='ml-0.5 mt-1 opacity-70 shrink-0' />
        </span>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        {selectedTeam ? (
          <>
            <Link to='/'>
              <DropdownMenuItem className='flex gap-4 w-full pr-4'>
                <div>View guild home</div>
              </DropdownMenuItem>
            </Link>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {teamsWithAccess.map((team) => (
          <Link key={team.id} to={customDomainTeamPath(customDomain, team)}>
            <DropdownMenuItem className='flex gap-4 w-full pr-4'>
              <div
                className={`flex items-center gap-1.5 ${
                  team.id === selectedTeam?.id ? 'text-primary' : ''
                }`}
              >
                {team.default ? <DefaultTeamStar /> : null}
                <span>{team.name}</span>
              </div>
            </DropdownMenuItem>
          </Link>
        ))}

        <LinkedTeamsMenuGroups groups={linkedGroups} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const CustomDomainSelectedTeamMenu = ({
  selectedTeam,
  guild,
  guildReference,
  variant = 'header',
}: {
  selectedTeam: wowaudit.TeamReference
  guild?: wowaudit.Guild
  guildReference?: wowaudit.GuildReference
  variant?: 'header' | 'mobile'
}) => {
  const { data: currentTeam } = useTeamDetails(selectedTeam.id)
  const teamReference = currentTeam
    ? { ...currentTeam, path: selectedTeam.path }
    : selectedTeam

  return (
    <GuildContext.Provider
      value={{
        guild,
        guildReference,
        currentTeam,
        teamReference,
      }}
    >
      <CustomDomainTeamMenu variant={variant} />
    </GuildContext.Provider>
  )
}

export const CustomDomainTeamMenu = ({
  variant = 'header',
}: {
  variant?: 'header' | 'mobile'
}) => {
  const { menuItems } = useTeamMenuItems()
  if (!menuItems.length) return null

  if (variant === 'mobile') {
    return <EntityMenuMobileList menuItems={menuItems} />
  }

  return <EntityMenu menuItems={menuItems} placement='header' />
}
